// src/modules/app-views/offers/pages/Document/tabs/RecordTab/OfferLandingPagesCard.jsx
// *******************************************************************************
// OFFER LANDING PAGES CARD
// *******************************************************************************
// Associates landing pages to the offer. The offer's linkedLandingPageIds array
// defines which leads (by landingPageId) are part of this offer's funnel.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback } from 'react';
import { Card, Col, Row } from 'antd';
import translateX from 'utils/translateX';
import SimpleMultiSelect from 'components/SimpleComponents/SimpleMultiSelect';
import axiosService from 'services/axiosService';
import { mergeUrlParamsQuery } from 'utils/urlUtils';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const LANDING_PAGES_URL = '/v1/landing-pages/get-by-field';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OfferLandingPagesCard = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFetch = useCallback(async searchValue => {
    const url = mergeUrlParamsQuery({
      url: LANDING_PAGES_URL,
      query: { field: 'name', value: searchValue, mode: 'partial' },
    });

    const [docs] = await axiosService.get({ url });

    const items = Array.isArray(docs) ? docs : docs ? [docs] : [];

    return items.map(lp => ({
      value: lp._id,
      label: lp.name || lp._id,
    }));
  }, []);

  const handleFetchByValue = useCallback(async id => {
    if (!id) return null;

    const [doc] = await axiosService.get({
      url: `/v1/landing-pages/${id}`,
    });

    if (!doc?._id) return null;

    return { value: doc._id, label: doc.name || doc._id };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card title={translateX('landing_pages')} style={{ marginTop: 16 }}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <SimpleMultiSelect
            name="linkedLandingPageIds"
            label="landing_pages"
            placeholder="select"
            onFetch={handleFetch}
            onFetchByValue={handleFetchByValue}
            minSearchLength={0}
            allowClear
          />
        </Col>
      </Row>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default OfferLandingPagesCard;
